// frontend/src/components/ServiceType/ServiceTypeCard.tsx
// Mobile-first service type card

import React from 'react';
import { Check, X, Eye, Edit, Trash2 } from 'lucide-react';
import { ServiceType } from '@/lib/api/serviceTypeService';
import { ActionsDropdown, createCommonActions } from '@/components/common/ui';

interface ServiceTypeCardProps {
  serviceType: ServiceType;
  onEdit: () => void;
  onDelete: () => void;
  onToggleStatus: () => void;
  onApprove?: () => void;
  onReject?: () => void;
}

const ServiceTypeCard: React.FC<ServiceTypeCardProps> = ({
  serviceType,
  onEdit,
  onDelete,
  onToggleStatus,
  onApprove,
  onReject
}) => {
  return (
    <div className="p-4 border-b border-gray-200 last:border-b-0">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900 truncate">
              {serviceType.name}
            </h3>
            <div className="ml-2 flex items-center space-x-1">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                serviceType.is_active
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {serviceType.is_active ? 'Active' : 'Inactive'}
              </span>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                serviceType.is_approved
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {serviceType.is_approved ? 'Approved' : 'Pending'}
              </span>
            </div>
          </div>
          
          <div className="mt-1">
            <p className="text-sm text-gray-600">{serviceType.category_name}</p>
            {serviceType.description && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                {serviceType.description}
              </p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              {serviceType.usage_count || 0} services using this type
            </p>
          </div>
        </div>
        
        <div className="ml-2 flex-shrink-0">
          <ActionsDropdown
            size="sm"
            actions={[
              createCommonActions.edit(onEdit),
              createCommonActions.toggleStatus(
                serviceType.is_active,
                onToggleStatus
              ),
              ...(!serviceType.is_approved && onApprove && onReject ? [
                createCommonActions.approve(onApprove),
                {
                  id: 'reject',
                  label: 'Reject',
                  icon: X,
                  onClick: onReject,
                  variant: 'danger' as const
                }
              ] : []),
              ...(serviceType.can_delete ? [
                createCommonActions.delete(onDelete)
              ] : [])
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default ServiceTypeCard;